import { defaultSchema } from 'rehype-sanitize';
import type { Options as SanitizeSchema } from 'rehype-sanitize';

/**
 * Sanitize allow-list for policy markdown (Terms of Use / Privacy Policy).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS DUPLICATED BYTE-FOR-BYTE IN TWO REPOS. KEEP THEM IN SYNC:
 *   - account-management-app/src/lib/markdownSanitize.ts  (Monarch Admin preview)
 *   - innovate-app/src/lib/markdownSanitize.ts            (public /terms, /privacy)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The admin preview and the public policy pages must render the *same*
 * document — same renderer (`@uiw/react-md-editor`'s `MDEditor.Markdown`), same
 * plugin pipeline, same allow-list. If they diverge, an author approves text
 * that is not the text a user signs.
 *
 * `MDEditor.Markdown` runs `rehype-raw` before any caller-supplied
 * `rehypePlugins`, so raw HTML embedded in policy content reaches the DOM
 * unless it is sanitized here.
 *
 * The base is `hast-util-sanitize`'s GitHub-style `defaultSchema`, which already
 * permits every GFM construct policy documents use: tables (`table`/`thead`/
 * `tbody`/`tr`/`th`/`td`), strikethrough (`del`/`s`/`strike`), task-list
 * checkboxes (`input[type=checkbox][disabled]` plus the `contains-task-list` /
 * `task-list-item` classes), autolinks (`a[href]` restricted to safe
 * protocols), and footnotes. `POLICY_GFM_TAG_NAMES` re-asserts that set
 * explicitly so an upstream default change cannot silently break rendering.
 *
 * Three deliberate extensions, all of them needed because the sanitizer runs at
 * the END of `MDEditor.Markdown`'s pipeline and therefore also sees the markup
 * the renderer's own trusted plugins injected. Without them the sanitizer would
 * quietly strip the renderer's decorations and the two surfaces would render
 * differently from the way the public page renders today:
 *
 *   1. `svg` / `path` with a fixed, inert attribute set — the heading-anchor
 *      octicon (`rehype-autolink-headings` + the `wmde` rewrite) and the
 *      code-block copy button. Nothing here can execute: no `script`, `use`,
 *      `animate`, `foreignObject`, `href`/`xlink:href` or `on*` attribute is
 *      permitted, so an author who pastes an `<svg>` gets an inert picture.
 *   2. The specific class names those decorations use, allow-listed by exact
 *      value rather than opening `className` generally.
 *   3. `clobberPrefix: ''` — the default rewrites every `id` to
 *      `user-content-<id>` while leaving `href="#<id>"` untouched, which
 *      silently breaks in-document section links in a legal document. Policy
 *      content is authored by `platform_admin` only, so DOM-clobbering is not
 *      the threat this sanitizer defends against; broken cross-references in a
 *      published policy is a real cost.
 *
 * Deliberately NOT allowed:
 *   - the `style` attribute anywhere. This costs GFM column alignment
 *     (`|:---:|` emits `style="text-align:center"`), which is cosmetic and is
 *     lost identically on both surfaces. It buys immunity from CSS-based
 *     overlay/clickjacking tricks in pasted HTML.
 *   - every `on*` inline event handler (not in the allow-list, so dropped).
 *   - `javascript:` and `data:` URLs (the default `protocols` allow-list).
 *   - `script`, `style`, `iframe`, `object`, `embed`, `noscript` and
 *     `template`, which are stripped *with their children* so their contents
 *     do not leak into the page as visible text.
 */

/** GFM element names policy content relies on; asserted on top of the default. */
export const POLICY_GFM_TAG_NAMES = [
  'a',
  'del',
  'input',
  'li',
  'ol',
  's',
  'strike',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
] as const;

/**
 * Element names removed together with their children. Anything here either
 * executes or carries content that must never surface as text.
 */
export const POLICY_STRIPPED_TAG_NAMES = [
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'noscript',
  'template',
] as const;

/**
 * The exact `class` values `MDEditor.Markdown`'s own plugins attach — the
 * heading-anchor wrapper, the code-block copy button, and their octicons.
 * Allow-listed by exact value rather than opening `class` generally.
 */
const ANCHOR_CLASS = 'anchor';
const COPY_BUTTON_CLASS = 'copied';
const OCTICON_CLASSES = ['octicon octicon-link', 'octicon-copy', 'octicon-check'] as const;

const defaultAttributes = defaultSchema.attributes ?? {};

export const POLICY_SANITIZE_SCHEMA: SanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultAttributes,
    '*': [...(defaultAttributes['*'] ?? []), 'ariaHidden'],
    a: [...(defaultAttributes.a ?? []), ['class', ANCHOR_CLASS]],
    div: [...(defaultAttributes.div ?? []), 'data-code', ['class', COPY_BUTTON_CLASS]],
    path: ['d', 'fillRule'],
    svg: ['fill', 'height', 'version', 'viewBox', 'width', ['className', ...OCTICON_CLASSES]],
  },
  clobberPrefix: '',
  tagNames: Array.from(
    new Set([...(defaultSchema.tagNames ?? []), ...POLICY_GFM_TAG_NAMES, 'path', 'svg'])
  ),
  strip: Array.from(
    new Set([...(defaultSchema.strip ?? []), ...POLICY_STRIPPED_TAG_NAMES])
  ),
};

export default POLICY_SANITIZE_SCHEMA;
